import axios from 'axios';
import { useEffect, useSyncExternalStore } from 'react';

// WHICH COLOURWAY THE MESHES ON SCREEN ARE PAINTED IN. A product and its components are kept once
// — one shape, drilled once — and the server lays a colourway's colours over it when it sends the
// file (see notice_3d._colourway_file). So a mesh's address names the colourway on screen, and that
// colourway's paint STAMP, which moves each time its colours are applied: the page holds meshes by
// address, and a repaint must be a new one.
//
// One colourway for the page at a time, the sidebar's (see useParkedSubmodelFilter), read where
// the address is made (meshSrc) rather than passed down through every viewer that makes one.
let inView = '';
const stamps = new Map<string, number>();
const listeners = new Set<() => void>();
let version = 0;
const notify = () => { version += 1; listeners.forEach((one) => one()); };
const subscribe = (one: () => void) => { listeners.add(one); return () => { listeners.delete(one); }; };

// SET DURING THE RENDER THAT READS IT, by the viewer above every phase — so the meshes drawn in
// that same render already ask for it; the subscribers are told after (see useColourwayInView).
export function setColourwayInView(submodel: string) {
  inView = submodel;
}

export function setColourwayStamp(submodel: string, stamp: number) {
  if (stamps.get(submodel) === stamp) return;
  stamps.set(submodel, stamp);
  notify();
}

// The query a shape's address carries: the colourway, and its paint's stamp. Empty with none.
// THE PAINTING CODE'S REVISION, in the address too: a painted mesh is kept by the browser for good at
// its address, so a change to how meshes are painted reaches nobody until it moves. 2: a colour over
// our grain map lifted, as the map was when it went on (Louis 2026-11-02).
const PAINT_REV = 2;

export const colourwayQuery = () => (inView
  ? `&colourway=${encodeURIComponent(inView)}&p=${stamps.get(inView) ?? 0}&r=${PAINT_REV}` : '');

// THE VIEWER'S HOOK: the colourway in view made current, this document's stamps read, and a render
// for every change of either — which redraws the phases under it with their meshes' new addresses.
//
// `onStamps`: told, once a document's stamps are in, which colourways have a paint at all.
export function useColourwayInView(
  docId: string, submodel: string, onStamps?: (painted: Set<string>) => void,
) {
  setColourwayInView(submodel);
  useSyncExternalStore(subscribe, () => version);
  useEffect(() => { notify(); }, [submodel]);
  useEffect(() => {
    let alive = true;
    axios.get<{ stamps: Record<string, number> }>(`/api/notice/3d/colourways/${encodeURIComponent(docId)}`)
      .then(({ data }) => {
        if (!alive) return;
        Object.entries(data.stamps || {}).forEach(([sub, stamp]) => stamps.set(sub, stamp));
        notify();
        onStamps?.(new Set(Object.keys(data.stamps || {})));
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);
}
